import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Helmet } from "react-helmet";

import TopBar from "../../components/common/public/TopBar";
import OrgPortalHeader from "../../components/organization/portal/OrgPortalHeader";
import DownloadApp from "../../components/common/public/DownloadApp";
import { formattedDateTime } from "../../util/date";
import { GlobalStyles } from "../../constants/styles";
import { GET_MINI_EVENTS_SEASON_ANCHORED } from "../../components/stages/queries/GetGames";
import PublicFooter from "../../components/common/public/Footer";
import useTranslation from "../../i18n/useTranslation";
import { useBranding } from "../../context/BrandingContext";
import OrgPortalFooter from "../../components/organization/portal/OrgPortalFooter";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const { colors: globalColors } = GlobalStyles;

function PublicSchedulePage() {
    const { t, language } = useTranslation();
    const { branding, theme, isOrgPortal, ready } = useBranding();
    const { seasonSlug } = useParams();

    const { data, loading, error } = useQuery(GET_MINI_EVENTS_SEASON_ANCHORED, {
        variables: { seasonSlug },
        fetchPolicy: "cache-first",
        skip: !ready,
    });

    const lastUpdated = useMemo(() => new Date().toLocaleString(), []);

    if (!ready) return null;

    const events = data?.publicEventsBySeason || [];
    const firstEvent = events[0];

    const org =
        firstEvent?.season?.division?.organization || branding?.organization || null;

    const colors = theme?.colors || {};

    const safeTheme = {
        primaryColor: colors.primary || "#14151A",
        secondaryColor: colors.secondary || colors.primary || "#14151A",
        accentColor: colors.accent || "#D5FF5F",

        backgroundColor: colors.background || "#05070A",
        surfaceColor: colors.surface || "#0B1220",
        cardBorder: colors.border || "rgba(255,255,255,0.06)",

        textOnBackground: colors.textOnBackground || "#E5E7EB",
        textOnSurface: colors.textOnSurface || "#F9FAFB",
        textOnPrimary: colors.textOnPrimary || "#F9FAFB",
        textOnSecondary:
            colors.textOnSecondary || colors.textOnPrimary || "#F9FAFB",

        textMuted: colors.textMuted || globalColors?.manatee || "#8d929d",
    };

    const pageBg = safeTheme.backgroundColor;
    const surface = safeTheme.surfaceColor;
    const accent = safeTheme.accentColor;
    const textMain = safeTheme.textOnBackground;
    const textOnSurface = safeTheme.textOnSurface;
    const textOnPrimary = safeTheme.textOnPrimary;
    const textMuted = safeTheme.textMuted;
    const borderColor = safeTheme.cardBorder;

    const brandingLogoUrl = branding?.logoUrl;
    const headerLogo = brandingLogoUrl || org?.logoUrl || theme?.logo?.main;

    const currentUrl =
        typeof window !== "undefined"
            ? window.location.href
            : `https://kbolasport.com/schedule/${seasonSlug}`;

    const orgName = firstEvent?.season?.division?.organization?.name || org?.name;
    const divisionName = firstEvent?.season?.division?.name;
    const seasonName = firstEvent?.season?.name;

    const titleParts = [orgName || null, divisionName || null, seasonName || null].filter(
        Boolean
    );

    const pageTitle = titleParts.length
        ? `${titleParts.join(" • ")} · ${t("publicSchedulePage.meta.schedule")} | ${t("publicSchedulePage.meta.pageTitleSuffix")}`
        : `${t("publicSchedulePage.meta.seasonSchedule")} · ${t("publicSchedulePage.meta.pageTitleSuffix")}`;

    const description = titleParts.length
        ? t("publicSchedulePage.meta.descriptionWithTitle", {
            title: titleParts.join(" • "),
        })
        : t("publicSchedulePage.meta.defaultDescription");

    const ogUrl =
        typeof window !== "undefined"
            ? window.location.href
            : `https://kbolasport.com/schedule/${seasonSlug}`;

    const ogImage =
        branding?.logoUrl ||
        branding?.organization?.logoUrl ||
        theme?.logo?.main ||
        "https://kbolasport.com/dist/img/angeonx-sports.png";

    const handleShare = () => {
        const url =
            typeof window !== "undefined"
                ? window.location.href
                : `https://kbolasport.com/schedule/${seasonSlug}`;

        if (typeof navigator !== "undefined" && navigator.share) {
            navigator
                .share({
                    title: pageTitle,
                    text: description,
                    url,
                })
                .catch(() => {});
            return;
        }

        if (
            typeof navigator !== "undefined" &&
            navigator.clipboard &&
            navigator.clipboard.writeText
        ) {
            navigator.clipboard.writeText(url).then(
                () => {
                    alert(t("publicSchedulePage.shareMessages.copied"));
                },
                () => {
                    alert(t("publicSchedulePage.shareMessages.copyFailed"));
                }
            );
            return;
        }

        window.prompt(t("publicSchedulePage.shareMessages.copyPrompt"), url);
    };

    const renderEventRow = (event) => {
        const eType = event?.eventType || "GAME";
        const game = event?.game;
        const training = event?.trainingSession;
        const competition = event?.competition;
        const generic = event?.genericEvent;

        const when = formattedDateTime(
            event?.date,
            event?.timezone,
            language || "en",
            "LLL d, yyyy HH:mm"
        );

        const fieldName =
            event?.field?.name || t("publicSchedulePage.eventLabels.emptyValue");

        const stageName = event?.stage?.name || "";
        const groupName = game?.group?.name || "";
        const stageGroup =
            [stageName, groupName].filter(Boolean).join(" • ") ||
            t("publicSchedulePage.eventLabels.emptyValue");

        const homeResting = game?.noPlay === true && !game?.homeTeam?.name;
        const visitorResting = game?.noPlay === true && !game?.visitorTeam?.name;

        const refsText =
            visitorResting || homeResting
                ? ""
                : (event?.responsibles || []).map((res) => res.fullName).join(", ");

        let typeLabel = t("publicSchedulePage.eventTypes.game");
        let title = "";

        if (eType === "TRAINING" || training) {
            typeLabel = t("publicSchedulePage.eventTypes.training");
            const teamNames =
                training?.teams
                    ?.map((tItem) => tItem?.team)
                    ?.filter(Boolean)
                    ?.map((tItem) => tItem.name)
                    ?.join(", ") || "";

            title = teamNames
                ? t("publicSchedulePage.eventLabels.teams", { teams: teamNames })
                : t("publicSchedulePage.eventLabels.trainingSession");
        } else if (competition) {
            typeLabel = t("publicSchedulePage.eventTypes.competition");
            title = event?.title || t("publicSchedulePage.eventLabels.competition");
        } else if (generic) {
            typeLabel = t("publicSchedulePage.eventTypes.event");
            title = event?.title || t("publicSchedulePage.eventLabels.event");
        } else {
            const visitorName =
                game?.visitorTeam?.name ||
                game?.visitorTeamAlias ||
                t("publicSchedulePage.eventLabels.visitor");
            const homeName =
                game?.homeTeam?.name ||
                game?.homeTeamAlias ||
                t("publicSchedulePage.eventLabels.home");

            title = `${visitorName} @ ${homeName}`;
        }

        const status = event?.status || "SCHEDULED";
        const showScore = status !== "SCHEDULED" && eType === "GAME";
        const scoreText =
            showScore && game
                ? `${game.scoreVisitor ?? 0} - ${game.scoreHome ?? 0}`
                : status === "SCHEDULED"
                    ? t("publicSchedulePage.eventLabels.scheduled")
                    : status;

        return (
            <tr key={event.id}>
                <td>{when}</td>
                <td>{typeLabel}</td>
                <td>{title}</td>
                <td>{stageGroup}</td>
                <td>{fieldName}</td>
                <td>{refsText || t("publicSchedulePage.eventLabels.emptyValue")}</td>
                <td>{scoreText}</td>
            </tr>
        );
    };

    const renderHeader = () => {
        if (isOrgPortal && branding && org) {
            return <OrgPortalHeader org={org} logo={headerLogo} theme={safeTheme} />;
        }

        return <TopBar imgPath={theme?.logo?.main} />;
    };

    const renderFooter = () => {
        if (isOrgPortal && branding) {
            return <OrgPortalFooter branding={branding} theme={safeTheme} />;
        }

        return <PublicFooter imgPath="/dist/img/" />;
    };

    if (loading) {
        return (
            <div
                className="kbs-page kbs-page--public-schedule d-flex flex-column"
                style={{
                    backgroundColor: pageBg,
                    minHeight: "100vh",
                    color: "textMain",
                    fontFamily: "Poppins, sans-serif",
                }}
            >
                {renderHeader()}
                <div className="container flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="kbs-loading text-center">
                        <div
                            className="spinner-border mb-3"
                            role="status"
                            style={{ color: accent }}
                        />
                        <div style={{ color: textMuted }}>
                            {t("publicSchedulePage.loading.title")}
                        </div>
                    </div>
                </div>
                {renderFooter()}
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="kbs-page kbs-page--public-schedule d-flex flex-column"
                style={{
                    backgroundColor: pageBg,
                    minHeight: "100vh",
                    color: textMain,
                    fontFamily: "Poppins, sans-serif",
                }}
            >
                {renderHeader()}
                <div className="container flex-grow-1 d-flex align-items-center justify-content-center">
                    <div
                        className="text-center p-4"
                        style={{
                            backgroundColor: surface,
                            borderRadius: 16,
                            border: `1px solid ${borderColor}`,
                            color: textOnSurface,
                            maxWidth: 560,
                            width: "100%",
                        }}
                    >
                        <h2 className="mb-2" style={{ color: accent }}>
                            {t("publicSchedulePage.error.title")}
                        </h2>
                        <p className="mb-0" style={{ color: textMuted }}>
                            {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                        </p>
                    </div>
                </div>
                {renderFooter()}
            </div>
        );
    }

    return (
        <div
            className="kbs-page kbs-page--public-schedule d-flex flex-column"
            style={{
                backgroundColor: pageBg,
                color: textMain,
                fontFamily: "Poppins, sans-serif",
                minHeight: "100vh",
            }}
        >
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={ogUrl} />

                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:site_name" content="AngeonX Sports" />
                <meta property="og:locale" content="en_CA" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={ogImage} />

                <meta name="robots" content="index,follow" />
            </Helmet>

            {renderHeader()}

            <main className="flex-grow-1">
                <div
                    className="container kbs-public-schedule-container"
                    style={{
                        maxWidth: 1200,
                        padding: "24px 16px 40px",
                        margin: "0 auto",
                    }}
                >
                    <header className="kbs-public-schedule-header mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="kbs-breadcrumb">
                            <div
                                style={{
                                    fontSize: 12,
                                    color: textMain,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                }}
                            >
                                <span style={{ opacity: 0.9 }}>
                                    {org?.name || "AngeonX Sports"}
                                </span>
                                <span style={{ opacity: 0.5 }}> / </span>
                                <span style={{ opacity: 0.9 }}>
                                    {t("publicSchedulePage.meta.schedule")}
                                </span>
                                <span style={{ opacity: 0.5 }}> / </span>
                                <span style={{ opacity: 1 }}>Season #{seasonSlug}</span>
                            </div>
                        </div>

                        <div className="kbs-schedule-header-actions d-flex align-items-center gap-2 no-print">
                            <div
                                style={{
                                    fontSize: 11,
                                    color: textMain,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.12em",
                                }}
                            >
                                {t("publicSchedulePage.header.lastUpdated", {
                                    value: lastUpdated,
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={handleShare}
                                style={{
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: 999,
                                    padding: "8px 14px",
                                    backgroundColor: surface,
                                    color: textOnSurface,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                }}
                            >
                                <i className="fas fa-link" style={{ fontSize: 12 }} />
                                <span>{t("publicSchedulePage.header.share")}</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => window.print()}
                                style={{
                                    border: `1px solid ${accent}`,
                                    borderRadius: 999,
                                    padding: "8px 18px",
                                    backgroundColor: accent,
                                    color: textOnPrimary,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    cursor: "pointer",
                                }}
                            >
                                {t("publicSchedulePage.header.printSchedule")}
                            </button>
                        </div>
                    </header>

                    <section className="mb-4">
                        <h3
                            className="mb-2"
                            style={{
                                fontSize: "1rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.15em",
                                color: textMain,
                            }}
                        >
                            {t("publicSchedulePage.sections.seasonSchedule")}
                        </h3>
                        <p
                            style={{
                                fontSize: 13,
                                color: textMuted,
                                maxWidth: 700,
                                marginBottom: 0,
                            }}
                        >
                            {t("publicSchedulePage.sections.liveScheduleDescription")}
                        </p>
                    </section>

                    <section
                        style={{
                            backgroundColor: surface,
                            borderRadius: 16,
                            border: `1px solid ${borderColor}`,
                            overflow: "hidden",
                        }}
                    >
                        <div style={{ overflowX: "auto" }}>
                            <table
                                className="table mb-0"
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: 13,
                                    color: textOnSurface,
                                    marginBottom: 0,
                                }}
                            >
                                <thead
                                    style={{
                                        backgroundColor: surface,
                                    }}
                                >
                                <tr>
                                    {[
                                        t("publicSchedulePage.table.headers.dateTime"),
                                        t("publicSchedulePage.table.headers.type"),
                                        t("publicSchedulePage.table.headers.matchSession"),
                                        t("publicSchedulePage.table.headers.stageGroup"),
                                        t("publicSchedulePage.table.headers.field"),
                                        t("publicSchedulePage.table.headers.responsible"),
                                        t("publicSchedulePage.table.headers.scoreStatus"),
                                    ].map((label) => (
                                        <th
                                            key={label}
                                            style={{
                                                padding: "14px 12px",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.12em",
                                                color: textMain,
                                                borderBottom: `1px solid ${borderColor}`,
                                                whiteSpace: "nowrap",
                                                backgroundColor: surface,
                                            }}
                                        >
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                                </thead>

                                <tbody>
                                {events.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{
                                                padding: "18px 12px",
                                                textAlign: "center",
                                                fontSize: 13,
                                                color: textMuted,
                                                backgroundColor: surface,
                                            }}
                                        >
                                            {t("publicSchedulePage.table.empty")}
                                        </td>
                                    </tr>
                                ) : (
                                    events.map((event) => {
                                        const row = renderEventRow(event);
                                        return (
                                            <tr
                                                key={event.id}
                                                className="kbs-schedule-row"
                                                style={{
                                                    borderBottom: `1px solid ${borderColor}`,
                                                }}
                                            >
                                                {row.props.children}
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>

                        <div
                            style={{
                                padding: "14px 16px",
                                borderTop: `1px solid ${borderColor}`,
                                backgroundColor: surface,
                            }}
                        >
                            <p
                                className="mb-0"
                                style={{ fontSize: 12, color: textMuted }}
                            >
                                {t("publicSchedulePage.sections.readOnlyNote")}
                            </p>
                        </div>
                    </section>

                    {!isOrgPortal && (
                        <section className="mt-4 no-print">
                            <DownloadApp />
                        </section>
                    )}
                </div>
            </main>

            {renderFooter()}

            <div className="kbs-print-footer">
                {t("publicSchedulePage.print.footer", { url: currentUrl })}
            </div>

            <div className="kbs-print-watermark">
                {t("publicSchedulePage.print.watermark")}
            </div>

            <style>{`
                .kbs-schedule-row td {
                    color: ${textOnSurface};
                    padding: 12px;
                    vertical-align: middle;
                    background: ${surface};
                }

                .kbs-schedule-row:last-child {
                    border-bottom: none !important;
                }

                @media print {
                    @page {
                        margin: 14mm;
                    }

                    body {
                        background: #ffffff !important;
                    }

                    .kbs-page--public-schedule {
                        background: #ffffff !important;
                        color: #000000 !important;
                        box-shadow: none !important;
                    }

                    .kbs-public-schedule-container {
                        max-width: 100% !important;
                        padding: 0 8px 8px !important;
                    }

                    .kbs-schedule-row td,
                    .table th,
                    .table td {
                        color: #000000 !important;
                        background: #ffffff !important;
                        border-bottom: 1px solid #dddddd !important;
                    }

                    .no-print {
                        display: none !important;
                    }

                    .kbs-print-watermark {
                        display: block !important;
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(-18deg);
                        font-size: 72px;
                        font-weight: 800;
                        color: rgba(0,0,0,0.04);
                        text-transform: uppercase;
                        letter-spacing: 0.2em;
                        pointer-events: none;
                        z-index: 0;
                    }

                    .kbs-print-footer {
                        display: block !important;
                        position: fixed;
                        bottom: 8mm;
                        left: 0;
                        right: 0;
                        text-align: center;
                        font-size: 10px;
                        color: #666666;
                    }
                }

                .kbs-print-footer,
                .kbs-print-watermark {
                    display: none;
                }
            `}</style>
        </div>
    );
}

export default PublicSchedulePage;
